/**
 * `/members` checkbox filters — the funnel button that sits beside the "+",
 * the grouped panel it opens, and the chip row naming what is ticked.
 *
 * The catalog is the server's (`DataResponse.filter_groups`): this file
 * renders whatever groups it is handed and never lists a filter of its own.
 * Every ticked box must hold — the server ANDs them — and the panel says so
 * in its header, because two boxes of one group (Lead + Trial) then find
 * nobody.
 *
 * The panel is portalled to `<body>` with fixed positioning. The members card
 * clips its overflow (`.data-browser-content { overflow-x: hidden }`), so a
 * panel hung inside it would be cut off exactly when it matters — after a
 * filter has shrunk the list to a few rows. On a desktop it drops from the
 * button's row and spans the content column (`alignTo`), so its edges line up
 * with the toolbar and the list under it; on a phone it is a modal bottom
 * sheet over a scrim, with its actions pinned at the foot.
 *
 * Keyboard: opening moves focus into the panel, Tab cycles inside it (it sits
 * at the end of `<body>`, past every row of the page), and Escape, Done, ×,
 * Show members or the scrim close it and hand focus back to the button — the
 * same contract as `SelectField`. A press outside closes it and leaves focus
 * where the press put it.
 */

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import type { DataFilterGroup } from "@layer/dashboard/types";
import { filterChips, filterSections } from "../lib/browse-filters";

/** Widest the desktop panel grows; past this, extra width is only air. */
const PANEL_MAX_WIDTH = 1440;
/** Gap kept between the panel and every viewport edge. */
const EDGE = 16;

/** The controls Tab moves between inside the panel, in DOM order. */
function tabbables(root: HTMLElement): HTMLElement[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>("button:not(:disabled), input:not(:disabled)"),
  );
}

interface BrowseFilterMenuProps {
  readonly groups: ReadonlyArray<DataFilterGroup>;
  /** The ticked tokens, in tick order. */
  readonly active: ReadonlyArray<string>;
  readonly onToggle: (key: string) => void;
  readonly onClear: () => void;
  /** Phone layout: a bottom sheet instead of a drop-down panel. */
  readonly sheet: boolean;
  /**
   * The element whose left and right edges the desktop panel lines up with —
   * the page's content column. Without it the panel hangs from the button.
   */
  readonly alignTo?: RefObject<HTMLElement | null>;
  /**
   * The funnel button's ref, when the page needs to focus it — the chip row
   * hands focus back to it once its last chip is removed. Attached as the
   * button's `ref`, so it is typed as React's element ref (its `current` is
   * already nullable).
   */
  readonly buttonRef?: RefObject<HTMLButtonElement>;
}

export function BrowseFilterMenu({
  groups,
  active,
  onToggle,
  onClear,
  sheet,
  alignTo,
  buttonRef,
}: BrowseFilterMenuProps): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  const ownButtonRef = useRef<HTMLButtonElement | null>(null);
  const triggerRef = buttonRef ?? ownButtonRef;
  const panelRef = useRef<HTMLDivElement | null>(null);
  const scrimRef = useRef<HTMLDivElement | null>(null);
  const [placement, setPlacement] = useState<CSSProperties | null>(null);
  // Set by a close that hands focus back to the button. The focus waits for
  // the commit that closes the panel, after the sheet's `inert` is lifted.
  const refocusButton = useRef<boolean>(false);
  const activeSet = new Set(active);
  const count = active.length;

  const close = (returnFocus: boolean): void => {
    refocusButton.current = returnFocus;
    setOpen(false);
  };

  // Desktop: drop the panel just under the button's row, across the content
  // column (or from the button when there is none), inside the viewport, and
  // place it again on a resize. A scroll of the page does not move it: the
  // panel stays where it opened, below the masthead, its head and actions in
  // view, however the list behind it scrolls or shrinks.
  useLayoutEffect(() => {
    if (!open || sheet) return undefined;
    const place = (): void => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (r === undefined) return;
      const vw = document.documentElement.clientWidth;
      const vh = window.innerHeight;
      const column = alignTo?.current?.getBoundingClientRect();
      const width = Math.min(
        PANEL_MAX_WIDTH,
        column !== undefined ? column.width : vw - EDGE * 2,
        vw - EDGE * 2,
      );
      const anchor = column !== undefined ? column.left : r.left;
      const left = Math.max(EDGE, Math.min(anchor, vw - width - EDGE));
      const top = r.bottom + 10;
      setPlacement({ top, left, width, maxHeight: Math.max(240, vh - top - EDGE) });
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [open, sheet, alignTo, triggerRef]);

  // Once the panel is showing (placed, on a desktop), focus moves into it so
  // the next Tab reaches its first control and a screen reader announces it.
  const ready = open && (sheet || placement !== null);
  useEffect(() => {
    if (ready) panelRef.current?.focus({ preventScroll: true });
  }, [ready]);

  // The phone sheet is modal: the page behind it is inert — no focus, no
  // taps, nothing for a screen reader — until it closes.
  useEffect(() => {
    if (!open || !sheet) return undefined;
    const root = document.getElementById("root");
    if (root === null) return undefined;
    root.inert = true;
    return () => {
      root.inert = false;
    };
  }, [open, sheet]);

  useEffect(() => {
    if (open || !refocusButton.current) return;
    refocusButton.current = false;
    triggerRef.current?.focus();
  }, [open, triggerRef]);

  // Close on a press outside the button and the panel (the scrim handles its
  // own), or on an Escape the panel itself did not take.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (ev: MouseEvent): void => {
      const t = ev.target as Node;
      if (triggerRef.current?.contains(t) === true) return;
      if (panelRef.current?.contains(t) === true) return;
      if (scrimRef.current?.contains(t) === true) return;
      close(false);
    };
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === "Escape") close(true);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, triggerRef]);

  const onPanelKey = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Escape") {
      // Kept to this panel: a page-level Escape handler (the nav's More
      // panel) must not act on it as well.
      e.preventDefault();
      e.stopPropagation();
      close(true);
      return;
    }
    if (e.key !== "Tab" || panelRef.current === null) return;
    const items = tabbables(panelRef.current);
    const first = items[0];
    const last = items[items.length - 1];
    if (first === undefined || last === undefined) return;
    const at = document.activeElement;
    if (e.shiftKey && (at === first || at === panelRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && at === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const summary =
    count === 0
      ? "Every box you tick must match"
      : `${count} on · every one must match`;

  const panel = (
    <>
      {sheet ? (
        <div
          ref={scrimRef}
          className="browse-filter-scrim"
          role="presentation"
          onClick={() => close(true)}
        />
      ) : null}
      <div
        ref={panelRef}
        className={`browse-filter-panel${sheet ? " browse-filter-panel--sheet" : ""}`}
        role="dialog"
        aria-label="Filter members"
        {...(sheet ? { "aria-modal": true } : {})}
        tabIndex={-1}
        onKeyDown={onPanelKey}
        style={sheet ? undefined : (placement ?? { visibility: "hidden" })}
      >
        <div className="browse-filter-head">
          <div className="browse-filter-heading">
            <span className="browse-filter-title">Filters</span>
            <span className="browse-filter-summary">{summary}</span>
          </div>
          {sheet ? (
            <button
              type="button"
              className="browse-filter-close"
              aria-label="Close filters"
              onClick={() => close(true)}
            >
              ×
            </button>
          ) : (
            <div className="browse-filter-actions">
              <button
                type="button"
                className="browse-filter-clear"
                disabled={count === 0}
                onClick={onClear}
              >
                Clear all
              </button>
              <button
                type="button"
                className="browse-filter-done"
                onClick={() => close(true)}
              >
                Done
              </button>
            </div>
          )}
        </div>
        {/* The body scrolls; each section's columns keep an auto height, since
            a height-capped multi-column box overflows SIDEWAYS instead of
            balancing its columns. */}
        <div className="browse-filter-body">
          {filterSections(groups).map((section) => (
            <div
              key={section.label ?? "lead"}
              className="browse-filter-section"
            >
              {section.label !== null ? (
                <div className="browse-filter-section-head">
                  <span className="browse-filter-section-title">
                    {section.label}
                  </span>
                </div>
              ) : null}
              <div className="browse-filter-columns">
                {section.groups.map((g) => {
                  const ticked = g.options.filter((o) =>
                    activeSet.has(o.key),
                  ).length;
                  return (
                    <section
                      key={g.key}
                      className="browse-filter-group"
                      aria-label={g.label}
                    >
                      <div className="browse-filter-group-head">
                        <span className="browse-filter-group-title">
                          {g.label}
                        </span>
                        {ticked > 0 ? (
                          <span className="browse-filter-group-count">
                            {ticked}
                          </span>
                        ) : null}
                      </div>
                      {g.hint !== undefined ? (
                        <p className="browse-filter-group-hint">{g.hint}</p>
                      ) : null}
                      <div className="browse-filter-options">
                        {g.options.map((o) => (
                          <label key={o.key} className="browse-filter-option">
                            <input
                              type="checkbox"
                              checked={activeSet.has(o.key)}
                              onChange={() => onToggle(o.key)}
                            />
                            <span>{o.label}</span>
                          </label>
                        ))}
                      </div>
                    </section>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        {sheet ? (
          <div className="browse-filter-foot">
            <button
              type="button"
              className="browse-filter-clear"
              disabled={count === 0}
              onClick={onClear}
            >
              Clear all
            </button>
            <button
              type="button"
              className="browse-filter-done"
              onClick={() => close(true)}
            >
              Show members
            </button>
          </div>
        ) : null}
      </div>
    </>
  );

  return (
    <div className="browse-filter">
      <button
        ref={triggerRef}
        type="button"
        className={`browse-filter-btn${count > 0 ? " browse-filter-btn--on" : ""}${
          open ? " open" : ""
        }`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={count > 0 ? `Filters, ${count} on` : "Filters"}
        title="Filter members"
        onClick={() => (open ? close(false) : setOpen(true))}
      >
        <svg
          className="browse-filter-icon"
          viewBox="0 0 24 24"
          width="18"
          height="18"
          aria-hidden="true"
        >
          <path
            d="M3.5 5h17l-6.5 7.6V19l-4 1.8v-8.2L3.5 5z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
        </svg>
        {count > 0 ? <span className="browse-filter-count">{count}</span> : null}
      </button>
      {open ? createPortal(panel, document.body) : null}
    </div>
  );
}

interface BrowseFilterChipsProps {
  readonly groups: ReadonlyArray<DataFilterGroup>;
  readonly active: ReadonlyArray<string>;
  readonly onRemove: (key: string) => void;
  readonly onClear: () => void;
  /**
   * Called once the last chip is gone by the keyboard's hand (its × or Clear
   * all), so focus lands somewhere useful — the page passes the funnel.
   */
  readonly onEmptied?: () => void;
}

/** One removable chip per ticked box, plus Clear all. Nothing when none is. */
export function BrowseFilterChips({
  groups,
  active,
  onRemove,
  onClear,
  onEmptied,
}: BrowseFilterChipsProps): JSX.Element | null {
  const removeButtons = useRef(new Map<string, HTMLButtonElement>());
  // The chip position a removal left focus at, applied once the row has
  // re-rendered without the removed chip.
  const focusAt = useRef<number | null>(null);
  const activeKey = active.join(",");

  useEffect(() => {
    const index = focusAt.current;
    if (index === null) return;
    focusAt.current = null;
    if (active.length === 0) {
      onEmptied?.();
      return;
    }
    const key = active[Math.min(index, active.length - 1)];
    if (key !== undefined) removeButtons.current.get(key)?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  if (active.length === 0) return null;
  return (
    <div className="browse-filter-chips" role="group" aria-label="Active filters">
      {filterChips(groups, active).map((chip, index) => (
        <span key={chip.key} className="browse-filter-chip">
          {chip.group !== null ? (
            <span className="browse-filter-chip-group">{chip.group}</span>
          ) : null}
          <span className="browse-filter-chip-label">{chip.label}</span>
          <button
            ref={(el) => {
              if (el !== null) removeButtons.current.set(chip.key, el);
              else removeButtons.current.delete(chip.key);
            }}
            type="button"
            className="browse-filter-chip-x"
            aria-label={`Remove ${chip.group !== null ? `${chip.group}: ` : ""}${chip.label}`}
            onClick={() => {
              focusAt.current = index;
              onRemove(chip.key);
            }}
          >
            ×
          </button>
        </span>
      ))}
      <button
        type="button"
        className="browse-filter-chips-clear"
        onClick={() => {
          focusAt.current = 0;
          onClear();
        }}
      >
        Clear all
      </button>
    </div>
  );
}
